import { ROUTE_PATHS } from '@constants/routes';
import { useLocalization } from '@lib/localization';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton, { type ListItemButtonProps } from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import { styled } from '@mui/material/styles';
import { useAppSelector } from '@store/hooks';
import { CogIcon, HomeIcon, ProjectDiagramIcon, ServerIcon, UsersIcon } from 'react-line-awesome';
import { Link, useLocation, type LinkProps } from 'react-router-dom';

const ListItemButtonLink = styled(ListItemButton)<ListItemButtonProps & LinkProps>(() => ({
  padding: '2px !important',
  height: '38px',
}));

const StyledStack = styled(Stack)({
  flexGrow: 1,
  padding: '8px',
  justifyContent: 'space-between',
});

const StyledList = styled(List)({
  gap: '0.375rem',
  padding: 0,
});

const listItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  backgroundColor: '#26272F',
  borderRadius: '.375rem',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  border: '1px solid rgb(54, 56, 67)',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const secondaryListItemIconStyles = {
  marginLeft: '6px',
  marginRight: '6px',
  width: '28px',
  height: '28px',
  marginInlineEnd: '.325rem',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  '& i': {
    fontSize: '18px',
    color: '#B5B7C8'
  },
};

const MenuContent: React.FC<{ toggleDrawer?: () => void }> = ({ toggleDrawer }) => {
  const authState = useAppSelector((state) => state.auth);
  const userRole = authState.user?.role ?? 'user';

  const { t } = useLocalization();
  const location = useLocation();
  const currentPath = location.pathname;

  const mainListItems = [
    { text: t('Common_Home'), icon: <HomeIcon />, url: ROUTE_PATHS.HOME },
    { text: t('Common_Projects'), icon: <ProjectDiagramIcon />, url: ROUTE_PATHS.FOLDERS },
    ...(['sysadmin', 'admin'].includes(userRole)
      ? [{ text: t('Common_Users'), icon: <UsersIcon />, url: ROUTE_PATHS.USERS }]
      : []
    ),
  ];

  const secondaryListItems = [
    ...(['sysadmin', 'admin'].includes(userRole)
      ? [{ text: t('Common_ServerConfig'), icon: <ServerIcon />, url: ROUTE_PATHS.SERVER_CONFIG }]
      : []
    ),
    { text: t('Common_Settings'), icon: <CogIcon />, url: ROUTE_PATHS.SETTINGS },
  ].filter(Boolean);

  return (
    <StyledStack>
      <StyledList dense>
        {mainListItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButtonLink
              component={Link}
              to={item.url}
              selected={currentPath === item.url}
              onClick={toggleDrawer}
            >
              <ListItemIcon sx={listItemIconStyles}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButtonLink>
          </ListItem>
        ))}
      </StyledList>
      <StyledList dense sx={{ gap: '.1rem' }}>
        {secondaryListItems.map((item, index) => (
          <ListItem key={index} disablePadding>
            <ListItemButtonLink
              component={Link}
              to={item.url}
              selected={currentPath === item.url}
              onClick={toggleDrawer}
            >
              <ListItemIcon sx={secondaryListItemIconStyles}>
                {item.icon}
              </ListItemIcon>
              <ListItemText primary={item.text} />
            </ListItemButtonLink>
          </ListItem>
        ))}
      </StyledList>
    </StyledStack>
  );
};

export default MenuContent;